/**
 * useGridBulkActions — the bar that appears once rows are ticked.
 *
 *   selection ──► selectedRows ──► count ──► isVisible ──► BulkActionToolbar
 *                       │                                        ▲
 *                       └── clearSelection (apiRef) ─────────────┘
 *
 * DEPENDS ON `useGridSelection`: it reads the same accumulated `selectedRows`
 * map, so a paged grid's bar counts every page, not just the visible one.
 *
 * The actions themselves stay a `bulkActions` ReactNode prop — pure
 * configuration owned by the domain grid, not state this hook can hold.
 */
import { useCallback } from 'react';
import type { GridApiRef } from '../DataGrid';
import type { BaseGridStateWithHelpers } from '../shared/baseGridTypes';

export type ShouldRenderBulkActionToolbar = (state: BaseGridStateWithHelpers, hasBulkActions: boolean) => boolean;

/** Default gate — actions exist AND at least one row is ticked. */
export const shouldRenderBulkActionToolbarDefault: ShouldRenderBulkActionToolbar = (state, hasBulkActions) =>
  hasBulkActions && Object.values(state.selectedRows).length > 0;

export type GridBulkActionsOptions = {
  apiRef?: GridApiRef;
  enhancedGridState: BaseGridStateWithHelpers;
  /** Whether the call site supplied any actions at all — the first half of the gate. */
  hasBulkActions: boolean;
  entityName?: string;
  entityNamePlural?: string;
  shouldRender?: ShouldRenderBulkActionToolbar;
};

export type GridBulkActionsState = {
  selectedRows: Record<string, unknown>;
  selectedCount: number;
  entityName?: string;
  entityNamePlural: string;
  isVisible: boolean;
  clearSelection: () => void;
};

export function useGridBulkActions({
  apiRef,
  enhancedGridState,
  hasBulkActions,
  entityName,
  entityNamePlural,
  shouldRender = shouldRenderBulkActionToolbarDefault,
}: GridBulkActionsOptions): GridBulkActionsState {
  const clearSelection = useCallback(() => {
    apiRef?.current?.setRowSelectionModel({ type: 'include', ids: new Set() });
  }, [apiRef]);

  return {
    selectedRows: enhancedGridState.selectedRows,
    selectedCount: Object.values(enhancedGridState.selectedRows).length,
    entityName,
    entityNamePlural: entityNamePlural ?? `${entityName}s`,
    isVisible: shouldRender(enhancedGridState, hasBulkActions) && !!apiRef,
    clearSelection,
  };
}
